import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    FormControl,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FullScreenLoader from '@/components/FullScreenLoader';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import SelectionCard from '@/components/SelectionCard';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useUsers } from '@/queries/users';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';

export default function TransactionType() {
    const [showTip, setShowTip] = useState(true);
    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
    const [
        errorData,
        formData,
        activeQuote,
        setFormData,
        setDefaultTemplateFormFields,
        fetchQuoteTemplateDataOnEveryStep,
        setErrorData,
        setLawyerSignature,
    ] = useQuoteIntakeSetupStore((state) => [
        state.errorData,
        state.formData,
        state.activeQuote,
        state.setFormData,
        state.setDefaultTemplateFormFields,
        state.fetchQuoteTemplateDataOnEveryStep,
        state.setErrorData,
        state.setLawyerSignature,
    ]);
    const { activeLawFirmId } = useLawFirmContext();
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;
    const { data: users = [] } = useUsers({ lawFirmId });
    const theme = useTheme();
    const { quoteIntakeId } = useParams();
    const [matterTypeDisabled, setMatterTypeDisabled] = useState({
        Purchase: false,
        Sale: false,
        Refinance: false,
        PrivateLender: false,
    });

    const quoteMainContainerCardStyles = {
        display: 'flex',
        gap: '24px',
        boxSizing: 'border-box',
        flexDirection: 'column',
        // (100vh - combined height, borders, paddings and margins of the header, footer and any margin, padding of the content area itself)
        maxHeight: 'calc(100vh - 168px)', // Todo: Move the heights of headers and footers to some sort of variables to better manage this
        minHeight: '400px',
        width: 'calc(100% - 64px)',
        padding: '32px',
        margin: '32px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        overflowY: 'auto',
    };

    const handleValueChange = (key: string, value: any): void => {
        if (key === 'assignedLawyer') {
            if (value) {
                setLawyerSignature(value, quoteIntakeId);
            }
            const selectedLawyer = users.find((user) => user.userId === value);
            //Check if selected lawyer's email matches any CC email
            if (
                selectedLawyer &&
                (formData.step2.contactsCC?.includes(selectedLawyer.email) ||
                    formData.step5.contactCC?.includes(selectedLawyer.email))
            ) {
                //Remove the matched CC email from the CC contacts list
                const updatedContactsCC3 = formData.step2.contactsCC?.filter(
                    (email) => email !== selectedLawyer.email,
                );
                const updatedContactsCC5 = formData.step5.contactCC?.filter(
                    (email) => email !== selectedLawyer.email,
                );

                // Update form data with filtered CC contacts
                setFormData('step2', {
                    ...formData.step2,
                    contactsCC: updatedContactsCC3,
                });
                setFormData('step5', {
                    ...formData.step5,
                    contactCC: updatedContactsCC5,
                });
                warningSnackbar(
                    'The CC member has been removed because they cannot be the same as the assigned user. Please update the CC field with a different contact.',
                );
            }
        }
        setFormData('step0', {
            ...formData.step0,
            [key]: value,
        });
        setErrorData({
            ...errorData,
            step0: {
                process: undefined,
                quoteTemplateName: undefined,
                quoteApproverId: undefined,
            },
        });
    };

    useEffect(() => {
        // Disable selected matter type if quoteIntakeId exists
        if (activeQuote.quoteTemplateId) {
            const disabledTypes = {
                Purchase: formData.step0.transactionType !== 'Purchase',
                Sale: formData.step0.transactionType !== 'Sale',
                Refinance: formData.step0.transactionType !== 'Refinance',
                PrivateLender: formData.step0.transactionType !== 'PrivateLender',
            };
            setMatterTypeDisabled(disabledTypes);
        } else if (quoteIntakeId) {
            const disabledTypes = {
                Purchase: formData.step0.transactionType !== 'Purchase',
                Sale: formData.step0.transactionType !== 'Sale',
                Refinance: formData.step0.transactionType !== 'Refinance',
                PrivateLender: formData.step0.transactionType !== 'PrivateLender',
            };
            setMatterTypeDisabled(disabledTypes);
        }
    }, [quoteIntakeId, activeQuote.quoteTemplateId]);

    const disabledProvince = activeQuote.quoteTemplateId && formData?.step0?.province !== '';

    const handleTransactionTypeChange = (transactionType: string) => {
        // Handle the restricted state for the edit mode here
        if (quoteIntakeId) {
            warningSnackbar('Changing matter type for existing template is not allowed', true);
            return;
        }
        setFormData('step0', {
            ...formData.step0,
            transactionType,
        });
        // Handle things that are dependent on the selected matter
        setDefaultTemplateFormFields(transactionType);
    };

    //Show snackbar
    useEffect(() => {
        if (formData.step0.showError && formData.step0.showError !== '') {
            errorSnackbar(formData.step0.showError);
        }
    }, [formData.step0.showError]);

    const assignedLawer = () => {
        if (formData.step0.assignedLawyer !== 0) {
            const selectedUser = users.find(
                (user) => user.userId === formData.step0.assignedLawyer,
            );
            return {
                label: selectedUser?.fullName,
                value: selectedUser?.userId,
                key: selectedUser?.userId,
            };
        }

        return undefined;
    };

    //Call fetch api for quote email template and intake email template
    const fetchDataForCC = async () => {
        if (quoteIntakeId) {
            await Promise.all([
                fetchQuoteTemplateDataOnEveryStep('step2', quoteIntakeId, '1', true),
                fetchQuoteTemplateDataOnEveryStep('step2', quoteIntakeId, '2', true),
                fetchQuoteTemplateDataOnEveryStep('step5', quoteIntakeId, '1', true),
                fetchQuoteTemplateDataOnEveryStep('step5', quoteIntakeId, '2', true),
            ]);
        }
    };

    //Call fetch api for step1
    const fetchData = async () => {
        if (quoteIntakeId && Object.keys(formData.step0.transactionTypePrevPayload).length === 0) {
            await fetchQuoteTemplateDataOnEveryStep('step0', quoteIntakeId);
        }
    };

    /*
     * We call fetchDataForCC() during the initialization of step 1 data.
     * This is because, when entering a template for the first time,
     * the data isn't initialized until reaching that step.
     * This ensures that CC contacts are properly updated when we modify the assigned lawyer
     * after entering an existing form.
     */

    useEffect(() => {
        const load = async () => {
            setIsFullScreenLoading(true);
            try {
                await Promise.all([fetchData(), fetchDataForCC()]);
            } catch (error) {
                console.error(error);
            } finally {
                setIsFullScreenLoading(false);
            }
        };
        void load();
    }, [quoteIntakeId]);

    useEffect(() => {
        if (formData?.step0?.assignedLawyer) {
            setLawyerSignature(formData.step0.assignedLawyer, quoteIntakeId);
        }
    }, [formData.step0.assignedLawyer]);
    return (
        <Box sx={quoteMainContainerCardStyles}>
            {isFullScreenLoading && <FullScreenLoader />}
            <div>
                <Typography variant="h1" component="h1">
                    Matter Type
                </Typography>
                <Typography variant="body1" component="p">
                    Let's create your personalized pricing logic for specific matter type.
                </Typography>
            </div>
            {showTip && (
                <Alert
                    icon={<InfoSolid />}
                    severity="info"
                    onClose={() => {
                        setShowTip(false);
                    }}
                >
                    <AlertTitle>Tip</AlertTitle>
                    During the setup process, you will create a pricing logic for one of the
                    available matter types based on your selected practice area. Once the initial
                    setup is complete, you will have the ability to create new templates for other
                    types or adjust existing ones. Access the 'Templates' tab on your sidebar to
                    manage and customize your pricing templates.
                </Alert>
            )}
            <FormControl>
                <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                    Select Practice Area
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <TextField
                            select
                            sx={{ width: '100%' }}
                            value={'RealEstate'}
                            label="Practice Area"
                            variant="outlined"
                            disabled={!!quoteIntakeId || !!activeQuote.quoteTemplateId}
                        >
                            <MenuItem value="Civil" disabled={true}>
                                Civil
                            </MenuItem>
                            <MenuItem value="Incorporation" disabled={true}>
                                Corporate
                            </MenuItem>
                            <MenuItem value="Criminal" disabled={true}>
                                Criminal
                            </MenuItem>
                            <MenuItem value="Family" disabled={true}>
                                Family
                            </MenuItem>
                            <MenuItem value="Immigration" disabled={true}>
                                Immigration
                            </MenuItem>
                            <MenuItem value="RealEstate">Real Estate</MenuItem>
                            <MenuItem value="WillsAndEstates" disabled={true}>
                                Wills & Estates
                            </MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </FormControl>
            <Typography variant="body1" component="p">
                Choose matter type
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={3}>
                <SelectionCard
                    isActive={formData.step0.transactionType === 'Purchase'}
                    matterType="Purchase"
                    disabled={matterTypeDisabled.Purchase}
                    maxWidth="25%"
                    onClick={() => handleTransactionTypeChange('Purchase')}
                />
                <SelectionCard
                    isActive={formData.step0.transactionType === 'Sale'}
                    matterType="Sale"
                    disabled={matterTypeDisabled.Sale}
                    maxWidth="25%"
                    onClick={() => handleTransactionTypeChange('Sale')}
                />
                <SelectionCard
                    isActive={formData.step0.transactionType === 'Refinance'}
                    matterType="Refinance"
                    disabled={matterTypeDisabled.Refinance}
                    maxWidth="25%"
                    onClick={() => handleTransactionTypeChange('Refinance')}
                />
                <SelectionCard
                    isActive={formData.step0.transactionType === 'PrivateLender'}
                    matterType="PrivateLender"
                    disabled={matterTypeDisabled.PrivateLender}
                    maxWidth="25%"
                    onClick={() => handleTransactionTypeChange('PrivateLender')}
                />
            </Stack>
            <Typography variant="body1" component="p">
                Choose Province
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <TextField
                        select
                        sx={{ width: '100%' }}
                        value={formData.step0.province}
                        onChange={(ev) => {
                            handleValueChange('province', ev.target.value);
                        }}
                        label="Province"
                        variant="outlined"
                        disabled={!!disabledProvince}
                    >
                        {[
                            'Alberta',
                            'British Columbia',
                            'Manitoba',
                            'New Brunswick',
                            'Newfoundland and Labrador',
                            'Nova Scotia',
                            'Ontario',
                            'Prince Edward Island',
                            'Quebec',
                        ].map((province) => (
                            <MenuItem
                                key={province}
                                value={province}
                                disabled={province !== 'Ontario' && province !== 'Alberta'}
                            >
                                {province}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            <Typography variant="body1" component="p">
                Name your template and assign a lawyer to it
            </Typography>
            <Grid container spacing="24px">
                <Grid item xs={12} sm={6} md={6}>
                    <TextField
                        value={formData.step0.templateName}
                        onChange={(ev) => {
                            handleValueChange('templateName', ev.target.value);
                        }}
                        fullWidth
                        error={!!errorData?.step0?.quoteTemplateName}
                        helperText={errorData?.step0?.quoteTemplateName}
                        placeholder="e.g. Sale quote template"
                        type="text"
                        variant="outlined"
                        label="Template name"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                    <Autocomplete
                        value={assignedLawer() || ''}
                        onChange={(_ev, values) => {
                            handleValueChange('assignedLawyer', values?.value);
                        }}
                        fullWidth
                        disablePortal
                        options={users.map((user) => ({
                            label: user.fullName,
                            value: user.userId,
                            key: user.userId,
                        }))}
                        renderInput={(params) => {
                            const newParams = { ...params };
                            if (params.inputProps.value === '[object Object]') {
                                newParams.inputProps.value = '';
                            }
                            return (
                                <TextField
                                    {...newParams}
                                    error={!!errorData.step0?.quoteApproverId}
                                    helperText={errorData.step0?.quoteApproverId}
                                    label="Assigned user"
                                />
                            );
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
